import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Avatar,
  IconButton,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Tooltip,
  Container
} from '@mui/material';
import {
  LocalHospital as CancerIcon,
  Science as ChemoIcon,
  Assignment as PlanIcon,
  Group as TumorBoardIcon,
  Biotech as TrialIcon,
  Warning as ToxicityIcon,
  Person as PatientIcon,
  ArrowForward as ArrowIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useUserData } from '../../hooks/useUserData';
import { getOncologyStats } from '../../services/oncologyService';
import { PageHeader, HeaderButton, StatsCard, PageContainer, ActionCard } from './oncologyStyles';
import { LocalHospital as OncologyIcon } from '@mui/icons-material';

// Stat Card Component - Compact version without icons
const StatCard = ({ title, value, color, onClick }) => (
  <StatsCard 
    sx={{ 
      height: '100%', 
      cursor: onClick ? 'pointer' : 'default',
      borderLeft: 3,
      borderColor: `${color}.main`,
    }}
    onClick={onClick}
  >
    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="h4" component="div" fontWeight="bold" color={`${color}.main`}>
        {value}
      </Typography>
    </CardContent>
  </StatsCard>
);

// Quick Action Card Component - Compact with tooltip
const QuickActionCard = ({ title, description, icon, color, onClick }) => (
  <Tooltip title={description} arrow placement="top">
    <ActionCard onClick={onClick}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Avatar sx={{ bgcolor: `${color}.light`, width: 36, height: 36 }}>
          {React.cloneElement(icon, { sx: { color: `${color}.main`, fontSize: 20 } })}
        </Avatar>
        <Typography variant="body2" fontWeight="medium" sx={{ flex: 1 }}>
          {title}
        </Typography>
        <ArrowIcon color="action" sx={{ fontSize: 18 }} />
      </CardContent>
    </ActionCard>
  </Tooltip>
);

const OncologyDashboard = () => {
  const navigate = useNavigate();
  const { defaultFacility } = useUserData();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    activeDiagnoses: 0,
    activeTreatmentPlans: 0,
    scheduledChemoCycles: 0,
    upcomingTumorBoards: 0,
    activeTrials: 0,
    grade3PlusToxicities: 0
  });
  const [tabValue, setTabValue] = useState(0);

  const loadStats = useCallback(async () => {
    if (!defaultFacility?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getOncologyStats(defaultFacility.id);
      setStats(data);
    } catch (err) {
      console.error('Error loading oncology stats:', err);
      setError('Failed to load oncology statistics. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [defaultFacility?.id]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (!defaultFacility) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <Alert severity="warning">
            Please select a facility to view the oncology dashboard.
          </Alert>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container maxWidth="lg">
        {/* Header with Back Navigation */}
        <PageHeader
          title="Oncology Dashboard"
          subtitle={`Comprehensive cancer care management for ${defaultFacility?.name}`}
          icon={<OncologyIcon />}
          backTo="/"
          actions={
            <Tooltip title="Refresh Statistics">
              <HeaderButton onClick={loadStats} disabled={loading} startIcon={<RefreshIcon />}>
                Refresh
              </HeaderButton>
            </Tooltip>
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Statistics Cards */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={6} sm={4} md={2}>
                <StatCard
                  title="Diagnoses"
                  value={stats.activeDiagnoses}
                  color="primary"
                  onClick={() => navigate('/oncology/diagnoses')}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <StatCard
                  title="Treatment Plans"
                  value={stats.activeTreatmentPlans}
                  color="success"
                  onClick={() => navigate('/oncology/treatment-plans')}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <StatCard
                  title="Chemo Cycles"
                  value={stats.scheduledChemoCycles}
                  color="info"
                  onClick={() => navigate('/oncology/chemotherapy')}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <StatCard
                  title="Tumor Boards"
                  value={stats.upcomingTumorBoards}
                  color="secondary"
                  onClick={() => navigate('/oncology/tumor-board')}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <StatCard
                  title="Active Trials"
                  value={stats.activeTrials}
                  color="warning"
                  onClick={() => navigate('/oncology/clinical-trials')}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <StatCard
                  title="Toxicities (3+)"
                  value={stats.grade3PlusToxicities}
                  color="error"
                  onClick={() => navigate('/oncology/toxicity')}
                />
              </Grid>
            </Grid>

            {/* Quick Actions */}
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Quick Actions
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={4}>
                <QuickActionCard
                  title="New Cancer Diagnosis"
                  description="Record a new cancer diagnosis with staging"
                  icon={<CancerIcon />}
                  color="primary"
                  onClick={() => navigate('/oncology/diagnosis/new')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <QuickActionCard
                  title="Create Treatment Plan"
                  description="Build a comprehensive treatment plan"
                  icon={<PlanIcon />}
                  color="success"
                  onClick={() => navigate('/oncology/treatment-plan/new')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <QuickActionCard
                  title="Schedule Chemotherapy"
                  description="Plan chemotherapy cycles and dosing"
                  icon={<ChemoIcon />}
                  color="info"
                  onClick={() => navigate('/oncology/chemotherapy/schedule')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <QuickActionCard
                  title="Record Toxicity"
                  description="Document adverse events with CTCAE grading"
                  icon={<ToxicityIcon />}
                  color="error"
                  onClick={() => navigate('/oncology/toxicity/new')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <QuickActionCard
                  title="Schedule Tumor Board"
                  description="Create a new tumor board meeting"
                  icon={<TumorBoardIcon />}
                  color="secondary"
                  onClick={() => navigate('/oncology/tumor-board/schedule')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <QuickActionCard
                  title="Find Clinical Trial"
                  description="Search for matching clinical trials"
                  icon={<TrialIcon />}
                  color="warning"
                  onClick={() => navigate('/oncology/clinical-trials/search')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <QuickActionCard
                  title="Episodes of Care"
                  description="View patient cancer treatment journeys"
                  icon={<TimelineIcon />}
                  color="primary"
                  onClick={() => navigate('/oncology/episodes')}
                />
              </Grid>
            </Grid>

            {/* Tabbed Content */}
            <Paper sx={{ mb: 3, borderRadius: '12px' }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange}
                sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
              >
                <Tab label="Today's Schedule" />
                <Tab label="Pending Actions" />
                <Tab label="Recent Activity" />
              </Tabs>
              
              <Box sx={{ p: 3 }}>
                {tabValue === 0 && (
                  <Box>
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      Today's chemotherapy infusions, appointments, and tumor board cases will appear here.
                    </Typography>
                  </Box>
                )}
                {tabValue === 1 && (
                  <Box>
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      Pending assessments, treatment approvals, and follow-up tasks will appear here.
                    </Typography>
                  </Box>
                )}
                {tabValue === 2 && (
                  <Box>
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      Recent diagnoses, treatment completions, and clinical activities will appear here.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Module Navigation Cards */}
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Oncology Modules
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={4}>
                <Card sx={{ borderRadius: '12px' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.light' }}>
                        <CancerIcon color="primary" />
                      </Avatar>
                      <Typography variant="h6">Cancer Registry</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Manage cancer diagnoses, TNM staging, biomarkers, and pathology reports.
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={() => navigate('/oncology/diagnoses')}>
                      View Diagnoses
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6} lg={4}>
                <Card sx={{ borderRadius: '12px' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'info.light' }}>
                        <ChemoIcon sx={{ color: 'info.main' }} />
                      </Avatar>
                      <Typography variant="h6">Chemotherapy</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Protocol management, cycle tracking, dose calculations, and administration records.
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={() => navigate('/oncology/chemotherapy')}>
                      Manage Chemotherapy
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <Card sx={{ borderRadius: '12px' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'secondary.light' }}>
                        <TumorBoardIcon sx={{ color: 'secondary.main' }} />
                      </Avatar>
                      <Typography variant="h6">Tumor Board</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Schedule meetings, submit cases, and document multidisciplinary recommendations.
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={() => navigate('/oncology/tumor-board')}>
                      Tumor Board
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <Card sx={{ borderRadius: '12px' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'warning.light' }}>
                        <TrialIcon sx={{ color: 'warning.main' }} />
                      </Avatar>
                      <Typography variant="h6">Clinical Trials</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Trial enrollment, eligibility screening, and protocol compliance tracking.
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={() => navigate('/oncology/clinical-trials')}>
                      Clinical Trials
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <Card sx={{ borderRadius: '12px' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'error.light' }}>
                        <ToxicityIcon sx={{ color: 'error.main' }} />
                      </Avatar>
                      <Typography variant="h6">Toxicity Management</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      CTCAE grading, adverse event tracking, and dose modification recommendations.
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={() => navigate('/oncology/toxicity')}>
                      Manage Toxicities
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <Card sx={{ borderRadius: '12px' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'success.light' }}>
                        <PatientIcon sx={{ color: 'success.main' }} />
                      </Avatar>
                      <Typography variant="h6">Survivorship Care</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Post-treatment care plans, surveillance schedules, and late effects monitoring.
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={() => navigate('/oncology/survivorship')}>
                      Survivorship Plans
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            </Grid>
          </>
        )}
      </Container>
    </PageContainer>
  );
};

export default OncologyDashboard;
